'use client';
import { useEffect, useMemo } from 'react';
import {
    DISPLAY_FONTS,
    normalizeGoogleFontWeights as normalizeWeights,
    sanitizeFontFamily,
    TEXT_FONTS,
} from '@/utils/google-fonts';

export { DISPLAY_FONTS, TEXT_FONTS };
const createLink = (fonts, doc) => {
    const families = fonts
        .reduce((acc, font) => {
            const family = String(font.font || '')
                .trim()
                .replace(/ +/g, '+');
            const weights = (font.weights || []).join(';');
            return [...acc, family + (weights && `:wght@${weights}`)];
        }, [])
        .join('&family=');
    if (!families) return null;
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
    return link;
};

const normalizeExtraFontEntry = (entry) => {
    if (typeof entry === 'string') {
        const font = sanitizeFontFamily(entry);
        return font ? { font, weights: [400] } : null;
    }
    if (!entry || typeof entry !== 'object') return null;
    const font = sanitizeFontFamily(entry.font);
    if (!font) return null;
    const weights = normalizeWeights(entry.weights || [400]);
    return { font, weights: weights.length ? weights : [400] };
};

const getFontList = (extraFonts = []) => {
    const fonts = [...DISPLAY_FONTS];
    TEXT_FONTS.forEach((font) => {
        if (!fonts.find((f) => f.font === font.font)) {
            fonts.push(font);
        }
    });

    const seen = new Set(fonts.map((font) => font.font.toLowerCase()));
    extraFonts.forEach((entry) => {
        const normalized = normalizeExtraFontEntry(entry);
        if (!normalized) return;
        const key = normalized.font.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        fonts.push(normalized);
    });

    return fonts;
};

export const GoogleFontLoader = ({ extraFonts = [], targetDocument }) => {
    const extraFontsKey = JSON.stringify(extraFonts || []);
    const sanitizedExtraFonts = useMemo(() => {
        try {
            const parsed = JSON.parse(extraFontsKey);
            const list = Array.isArray(parsed) ? parsed : [];
            const byFamily = new Map();
            list.forEach((entry) => {
                const normalized = normalizeExtraFontEntry(entry);
                if (!normalized) return;
                const key = normalized.font.toLowerCase();
                const current = byFamily.get(key);
                if (!current) {
                    byFamily.set(key, normalized);
                    return;
                }
                byFamily.set(key, {
                    font: normalized.font,
                    weights: normalizeWeights([
                        ...current.weights,
                        ...normalized.weights,
                    ]),
                });
            });
            return Array.from(byFamily.values());
        } catch {
            return [];
        }
    }, [extraFontsKey]);

    useEffect(() => {
        const doc =
            targetDocument ||
            (typeof document !== 'undefined' ? document : null);
        if (!doc) return undefined;

        const fonts = getFontList(sanitizedExtraFonts);
        const link = createLink(fonts, doc);
        if (!link) return undefined;
        doc.head.appendChild(link);
        return () => {
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        };
    }, [sanitizedExtraFonts, targetDocument]);
    return null;
};
